/**
 * Integer-cents money arithmetic for display-side totals (journal entry
 * totals, the manual entry balance guard). Amounts are parsed from their
 * decimal strings without going through floating point, so 0.10 + 0.20 is
 * exactly 30 cents. The server stays the authority on every posted amount.
 */

const USER_AMOUNT = /^(\d*)(?:\.(\d{0,2}))?$/;
const SERVER_DECIMAL = /^(-?)(\d*)(?:\.(\d*))?$/;

/**
 * Cents for an amount typed by a user: digits with at most 2 decimals
 * ("12", "12.3", "12.34", ".5"). Commas and a leading "$" are tolerated.
 * Returns null for blank input, negatives, exponents, more than 2 decimals or
 * anything else that is not a plain non-negative amount.
 */
export function parseAmountCents(input: string | null | undefined): number | null {
  const cleaned = (input ?? '').trim().replace(/^\$/, '').replace(/,/g, '');
  const match = USER_AMOUNT.exec(cleaned);
  if (!match) return null;
  const [, whole, fraction = ''] = match;
  if (!whole && !fraction) return null;
  const cents = Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
}

/**
 * Cents for a decimal string from the server ("100", "12.3", "-0.50").
 * Digits past the second decimal are dropped (the API sends at most 2).
 * Returns null when the value is not a decimal string.
 */
export function decimalToCents(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const match = SERVER_DECIMAL.exec(String(value).trim());
  if (!match) return null;
  const [, sign, whole, fraction = ''] = match;
  if (!whole && !fraction) return null;
  const cents = Number(whole || '0') * 100 + Number(fraction.slice(0, 2).padEnd(2, '0'));
  if (!Number.isSafeInteger(cents)) return null;
  return sign && cents !== 0 ? -cents : cents;
}

/** Decimal string with 2 places for an integer number of cents (1234 -> "12.34", -5 -> "-0.05"). */
export function centsToDecimal(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(cents));
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Sum of decimal strings in integer cents; unparseable values count as 0. */
export function sumDecimals(values: Array<string | number | null | undefined>): number {
  return values.reduce<number>((total, value) => total + (decimalToCents(value) ?? 0), 0);
}
